import { useCallback, useRef } from 'react';

const CLIENT_KEY_PREFIX = 'pending-section';

/**
 * Registry of the section creates that have not answered yet. It owns everything about a
 * pending row's identity:
 *
 * - the `clientKey` it is rendered under and addressed by until a real UUID exists,
 * - the field edits queued while the create is in flight, flushed as one POST afterwards,
 * - a Delete/Archive confirmed before the create answered, deferred until it does,
 * - the temporary-id → real-UUID mapping, so a handler that captured the temporary id (a
 *   blur landing just as the create settles) still targets the real section.
 *
 * All of it lives in refs on purpose: a create that is already awaiting must read the newest
 * registry state, not the state of the render that started it.
 *
 * @returns {{
 *   createClientKey: () => string,
 *   registerPendingCreate: (clientKey: string) => void,
 *   isPendingCreate: (sectionKey: string) => boolean,
 *   queuePendingFields: (clientKey: string, uiFields: object) => void,
 *   requestPendingRemoval: (clientKey: string, action: string, index: number) => void,
 *   settlePendingCreate: (clientKey: string, createdSectionId?: string | null) => {
 *     queuedFields: object,
 *     removalAction: string | null,
 *     removalIndex: number,
 *   },
 *   resolveSectionId: (sectionId: string) => string,
 * }}
 */
export const usePendingSectionCreates = () => {
  const clientKeyCounterRef = useRef(0);
  const pendingCreatesRef = useRef(new Map());
  const resolvedSectionIdsRef = useRef(new Map());

  const createClientKey = useCallback(() => {
    clientKeyCounterRef.current += 1;

    return `${CLIENT_KEY_PREFIX}-${Date.now()}-${clientKeyCounterRef.current}`;
  }, []);

  const registerPendingCreate = useCallback((clientKey) => {
    pendingCreatesRef.current.set(clientKey, {
      queuedFields: {},
      removalAction: null,
      removalIndex: -1,
    });
  }, []);

  const isPendingCreate = useCallback(
    (sectionKey) => pendingCreatesRef.current.has(sectionKey),
    [],
  );

  /** Re-editing a field overwrites the queued value, so the flush sends the last one only. */
  const queuePendingFields = useCallback((clientKey, uiFields) => {
    const entry = pendingCreatesRef.current.get(clientKey);

    if (entry) {
      pendingCreatesRef.current.set(clientKey, {
        ...entry,
        queuedFields: { ...entry.queuedFields, ...uiFields },
      });
    }
  }, []);

  /** `index` is where the row sat when it was removed, so the response can land back there. */
  const requestPendingRemoval = useCallback((clientKey, action, index) => {
    const entry = pendingCreatesRef.current.get(clientKey);

    if (entry) {
      pendingCreatesRef.current.set(clientKey, {
        ...entry,
        removalAction: action,
        removalIndex: index,
      });
    }
  }, []);

  /**
   * Closes a pending create and hands back whatever accumulated while it was in flight.
   * Passing the created id records the temporary-id mapping; omitting it (a failed create)
   * simply forgets the row.
   */
  const settlePendingCreate = useCallback((clientKey, createdSectionId = null) => {
    const entry = pendingCreatesRef.current.get(clientKey);

    pendingCreatesRef.current.delete(clientKey);

    if (createdSectionId) {
      resolvedSectionIdsRef.current.set(clientKey, createdSectionId);
    }

    return entry ?? { queuedFields: {}, removalAction: null, removalIndex: -1 };
  }, []);

  /** Unknown ids pass through untouched, so this is safe to apply to every section id. */
  const resolveSectionId = useCallback(
    (sectionId) => resolvedSectionIdsRef.current.get(sectionId) ?? sectionId,
    [],
  );

  return {
    createClientKey,
    registerPendingCreate,
    isPendingCreate,
    queuePendingFields,
    requestPendingRemoval,
    settlePendingCreate,
    resolveSectionId,
  };
};
